import { apiGet, apiPost } from "@/services/http";
import { endpoints } from "./endpoints";
import type { ApiEnvelope, ApiErrorBody } from "@/types/api";
import { ApiError } from "@/services/http/errors";

export type CallLogDirection = "incoming" | "outgoing" | "missed";

export type CallLogDto = {
  id: number;
  organization?: number | null;
  user?: number | null;
  direction: CallLogDirection;
  number: string;
  display_name?: string;
  started_at: string;
  ended_at?: string | null;
  duration_seconds?: number;
  answered?: boolean;
  client_id?: string | null;
  created_at?: string;
};

export type CreateCallLogPayload = {
  direction: CallLogDirection;
  number: string;
  display_name?: string;
  started_at: string;
  ended_at?: string | null;
  duration_seconds?: number;
  answered?: boolean;
  client_id?: string;
};

type CallLogsListResponse =
  | (ApiEnvelope<CallLogDto[]> & { count?: number; success?: boolean })
  | {
      count?: number;
      next?: string | null;
      previous?: string | null;
      data: CallLogDto[];
      message?: string;
      success?: boolean;
    };

export async function fetchCallLogs(params?: {
  direction?: CallLogDirection | "all";
  search?: string;
  number?: string;
  size?: number;
}) {
  const query = new URLSearchParams();
  if (params?.direction && params.direction !== "all") {
    query.set("direction", params.direction);
  }
  if (params?.search?.trim()) {
    query.set("search", params.search.trim());
  }
  if (params?.number?.trim()) {
    query.set("number", params.number.trim());
  }
  query.set("size", String(params?.size ?? 200));
  query.set("ordering", "-started_at");

  const qs = query.toString();
  const url = `${endpoints.callLogs}${qs ? `?${qs}` : ""}`;
  const response = await apiGet<CallLogsListResponse>(url);
  const rows = Array.isArray(response?.data) ? response.data : [];
  if (!Array.isArray(response?.data)) {
    throw new ApiError(0, response, "Could not load call logs.");
  }
  return rows;
}

export async function createCallLog(payload: CreateCallLogPayload) {
  const response = await apiPost<
    ApiEnvelope<CallLogDto> & ApiErrorBody,
    CreateCallLogPayload
  >(endpoints.callLogs, payload);

  if (!response?.data) {
    throw new ApiError(
      0,
      response,
      response?.error?.message || response?.message || "Could not save call log.",
    );
  }

  return response.data;
}
